import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { countyService } from '../../services/countyService';
import { leagueService, type LeagueSeason } from '../../services/leagueService';
import { ownershipService, type AvailableDate } from '../../services/ownershipService';
import { recentConquestsService, type RecentConquest } from '../../services/recentConquestsService';
import { teamService } from '../../services/teamService';
import type { Team } from '../../types';
import { createOwnMapEngine, type OwnMapEngine } from './ownMapEngine';
import { countyAreaSqMi, FIPS_ALIASES, loadBaseGeo, shortTeamName, type CountyTopology } from './ownMapGeo';
import type { OwnMapOwnership, OwnMapSelection, OwnMapViewMode } from './ownMapTypes';
import { pickMapFillColor } from './ownMapTypes';
import { enlistService } from '../../services/enlistService';
import type { PartnerSkin } from '../../partners/types';
import TerritoryDrawer from './TerritoryDrawer';
import BroadcastShell from './BroadcastShell';
import OwnMapShareControls from './OwnMapShareControls';
import { BROADCAST_CONFERENCES, conferenceMatches } from './broadcastConferences';
import { formatCompactPop, formatSqMi, rankEmpiresByCounties } from './empireStats';
import { getTodayEastern } from '../../utils/date';
import './OwnMap.css';

/** NCAA men's D1 — product home map + optional /lab/own-map debug chrome. */
const LEAGUE_ID = 'ncaa_basketball';

const FALLBACK_TICKS = ['NOV', 'DEC', 'JAN', 'FEB', 'MAR', 'APR', 'CHAMPION'];

export type OwnMapVariant = 'home' | 'lab' | 'partner';

function toOwnMapOwnership(
  rows: Awaited<ReturnType<typeof ownershipService.getOwnershipByLeague>>['ownership'],
  snapshotDate: string | null,
  rosterById: Map<number, Team>
): OwnMapOwnership {
  const ownership: Record<string, number> = {};
  const teams: OwnMapOwnership['teams'] = {};
  for (const row of rows) {
    const fips = String(row.county_fips).padStart(5, '0');
    const teamId = Number(row.team_id);
    ownership[fips] = teamId;
    if (!teams[teamId]) {
      const roster = rosterById.get(teamId);
      const espn =
        row.espn_team_id != null
          ? Number(row.espn_team_id)
          : roster?.espn_team_id != null
            ? Number(roster.espn_team_id)
            : null;
      // Roster (live teams table) wins over snapshot-frozen display fields.
      const logo = roster?.logo_url || row.logo_url || null;
      const name = roster?.team_name || row.team_name || `Team ${teamId}`;
      teams[teamId] = {
        id: teamId,
        name,
        short: shortTeamName(roster?.short_name || row.short_name || name),
        color: pickMapFillColor({
          mapColor: roster?.map_color ?? row.map_color,
          primary: roster?.primary_color ?? row.primary_color,
          secondary: roster?.secondary_color ?? row.secondary_color,
        }),
        espn_team_id: Number.isFinite(espn as number) ? espn : null,
        logo_url: logo,
        conference: roster?.conference || null,
      };
    }
  }
  for (const [geoFips, dbFips] of Object.entries(FIPS_ALIASES)) {
    if (ownership[dbFips] != null && ownership[geoFips] == null) {
      ownership[geoFips] = ownership[dbFips];
    }
  }
  return { ownership, teams, snapshotDate };
}

/** Gray land so partner faces can pin before the ownership API answers. */
function neutralOwnership(us: CountyTopology): OwnMapOwnership {
  const ownership: Record<string, number> = {};
  for (const g of us.objects.counties.geometries) {
    if (g.id == null) continue;
    ownership[String(g.id).padStart(5, '0')] = 1;
  }
  return {
    ownership,
    teams: {
      '1': { id: 1, name: 'Map', short: 'Map', color: '#243044' },
    },
    snapshotDate: null,
  };
}

/** Normalize API / Date / ISO strings to YYYY-MM-DD for scrubber matching. */
function toYmd(raw: string | Date | null | undefined): string {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return '';
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const t = Date.parse(s);
  if (!Number.isNaN(t)) return toYmd(new Date(t));
  return '';
}

function parseYmd(ymd: string): Date {
  const [y, m, d] = toYmd(ymd).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/** Index of selectedDate in snapshot list, or nearest date if not an exact hit. */
function resolveDateIndex(selected: string, dateList: AvailableDate[]): number {
  if (!dateList.length) return -1;
  const ymd = toYmd(selected);
  if (ymd) {
    const exact = dateList.findIndex((d) => toYmd(d.snapshot_date) === ymd);
    if (exact >= 0) return exact;
  }
  const start = toYmd(dateList[0].snapshot_date);
  const end = toYmd(dateList[dateList.length - 1].snapshot_date);
  const nearest = positionToNearestDate(
    ymd ? dateToPosition(ymd, start, end) : 1,
    dateList,
    start,
    end
  );
  return dateList.findIndex((d) => toYmd(d.snapshot_date) === toYmd(nearest));
}

function normalizeDateList(dateList: AvailableDate[]): AvailableDate[] {
  const todayET = getTodayEastern();
  return dateList
    .map((d) => ({ ...d, snapshot_date: toYmd(d.snapshot_date) }))
    .filter((d) => !!d.snapshot_date && d.snapshot_date <= todayET);
}

function seasonWindow(
  dates: AvailableDate[],
  season: LeagueSeason | null
): { start: string; end: string } {
  if (dates.length) {
    return {
      start: toYmd(dates[0].snapshot_date),
      end: toYmd(dates[dates.length - 1].snapshot_date),
    };
  }
  if (season?.preseason_date) {
    const start = season.preseason_date.slice(0, 10);
    const t = parseYmd(start);
    t.setMonth(t.getMonth() + 5);
    const end = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(
      t.getDate()
    ).padStart(2, '0')}`;
    return { start, end };
  }
  return { start: '2026-11-02', end: '2027-04-05' };
}

function dateToPosition(ymd: string, start: string, end: string): number {
  const t0 = parseYmd(start).getTime();
  const t1 = parseYmd(end).getTime();
  if (t1 <= t0) return 0;
  const t = parseYmd(ymd).getTime();
  return Math.max(0, Math.min(1, (t - t0) / (t1 - t0)));
}

function positionToNearestDate(p: number, dates: AvailableDate[], start: string, end: string): string {
  if (!dates.length) return '';
  const t0 = parseYmd(start).getTime();
  const t1 = parseYmd(end).getTime();
  const target = t0 + Math.max(0, Math.min(1, p)) * Math.max(1, t1 - t0);
  let best = dates[0].snapshot_date;
  let bestDist = Infinity;
  for (const d of dates) {
    const dist = Math.abs(parseYmd(d.snapshot_date).getTime() - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = d.snapshot_date;
    }
  }
  return best;
}

function ticksForWindow(start: string, end: string): string[] {
  const t0 = parseYmd(start);
  const t1 = parseYmd(end);
  if (Number.isNaN(t0.getTime()) || Number.isNaN(t1.getTime()) || t1 <= t0) {
    return FALLBACK_TICKS;
  }
  const months: string[] = [];
  const cur = new Date(t0.getFullYear(), t0.getMonth(), 1);
  const endM = new Date(t1.getFullYear(), t1.getMonth(), 1);
  while (cur <= endM && months.length < 8) {
    months.push(cur.toLocaleDateString('en-US', { month: 'short' }).toUpperCase());
    cur.setMonth(cur.getMonth() + 1);
  }
  if (months.length >= 2) {
    months[months.length - 1] = 'CHAMPION';
  }
  return months.length ? months : FALLBACK_TICKS;
}

function formatSeasonLabel(s: LeagueSeason): string {
  const raw = (s.label || String(s.season_year)).trim();
  if (/mbb|basketball/i.test(raw)) return raw.toUpperCase();
  return `${raw} MBB`.toUpperCase();
}

function pickDefaultSeason(list: LeagueSeason[], urlSeason: string | null): number | null {
  if (!list.length) return null;
  if (urlSeason && list.some((s) => String(s.season_year) === urlSeason)) {
    return Number(urlSeason);
  }
  const current = list.find((s) => s.is_current);
  if (current) return current.season_year;
  const withGames = list.find((s) => s.has_games);
  return (withGames || list[0]).season_year;
}

function trimColor(hex: string): string {
  const h = (hex || '#2b2f35').replace('#', '');
  if (h.length !== 6) return '#d9dde2';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (r * 299 + g * 587 + b * 114) / 1000;
  return lum > 140 ? '#111111' : '#d9dde2';
}

export type OwnMapLabProps = {
  variant?: OwnMapVariant;
  partner?: PartnerSkin | null;
  /** Partner CTA (open join panel, etc.). */
  onPartnerCta?: () => void;
  /** Overlay rendered inside the map stage (fan faces). Partner routes only. */
  mapOverlay?: React.ReactNode;
  /** Hide team logos/labels — partner fan maps. */
  hideTeamLogos?: boolean;
  /** Notify parent when the own-map engine is ready / torn down. */
  onEngine?: (engine: OwnMapEngine | null) => void;
  /** Ownership painted — safe to focus counties / teams. */
  onMapReady?: () => void;
  /**
   * Real territory ownership for partner survival culls.
   * Skips the gray placeholder paint used before the API answers.
   */
  onOwnershipChange?: (own: OwnMapOwnership | null) => void;
};

const OwnMapLab: React.FC<OwnMapLabProps> = ({
  variant = 'lab',
  partner = null,
  onPartnerCta,
  mapOverlay = null,
  hideTeamLogos = false,
  onEngine,
  onMapReady,
  onOwnershipChange,
}) => {
  const isHome = variant === 'home' || variant === 'partner';
  const isPartner = variant === 'partner';
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDateBoot = toYmd(searchParams.get('date'));
  const urlFocusBoot = (() => {
    const n = Number(searchParams.get('focusTeam'));
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const stageRef = useRef<HTMLElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const seamsRef = useRef<SVGPathElement | null>(null);
  const selRef = useRef<SVGPathElement | null>(null);
  const labelsRef = useRef<SVGGElement | null>(null);
  const logosRef = useRef<HTMLDivElement | null>(null);
  const rosterRef = useRef<Map<number, Team>>(new Map());
  const engineRef = useRef<OwnMapEngine | null>(null);
  const loadedKeyRef = useRef<string | null>(null);
  /** Season that bootstrap already painted; skip the season-change wipe on first set. */
  const bootstrappedSeasonRef = useRef<number | null | undefined>(undefined);
  const focusedTeamIdRef = useRef<number | null>(urlFocusBoot);
  /** Zoom once for deep-linked focusTeam; later date loads only re-highlight. */
  const deepLinkFocusZoomRef = useRef(urlFocusBoot != null);
  const [engineReady, setEngineReady] = useState(false);

  const [view, setView] = useState<OwnMapViewMode>('flat');
  const [seasons, setSeasons] = useState<LeagueSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [dates, setDates] = useState<AvailableDate[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(urlDateBoot);
  const [focusedTeamId, setFocusedTeamId] = useState<number | null>(urlFocusBoot);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState<OwnMapSelection | null>(null);
  const [countyCount, setCountyCount] = useState(0);
  const [popByFips, setPopByFips] = useState<Record<string, number | null>>({});
  const [areaSqMiByFips, setAreaSqMiByFips] = useState<Record<string, number>>({});
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<{ fips: string; label: string; search: string }[]>([]);
  const [activeHit, setActiveHit] = useState(0);
  const searchAllRef = useRef<{ fips: string; label: string; search: string }[]>([]);
  const [countySoldiers, setCountySoldiers] = useState<
    { handle: string; tier: string; team_name?: string }[]
  >([]);
  const [ownershipState, setOwnershipState] = useState<OwnMapOwnership | null>(null);
  const [countyLabels, setCountyLabels] = useState<Record<string, string>>({});
  const [activeConference, setActiveConference] = useState('all');
  const [conquests, setConquests] = useState<RecentConquest[]>([]);
  const [chromeHidden, setChromeHidden] = useState(false);
  const ownershipStateRef = useRef<OwnMapOwnership | null>(null);
  ownershipStateRef.current = ownershipState;
  focusedTeamIdRef.current = focusedTeamId;

  // Partner survival culls need real ownership — skip the gray placeholder paint.
  useEffect(() => {
    if (!onOwnershipChange) return;
    if (!ownershipState) {
      onOwnershipChange(null);
      return;
    }
    const teamIds = Object.keys(ownershipState.teams || {});
    const isPlaceholder =
      teamIds.length === 1 &&
      Number(teamIds[0]) === 1 &&
      ownershipState.teams['1']?.name === 'Map';
    onOwnershipChange(isPlaceholder ? null : ownershipState);
  }, [ownershipState, onOwnershipChange]);

  const handlePick = (sel: OwnMapSelection | null) => {
    setPick(sel);
    const tid = sel?.team?.id != null ? Number(sel.team.id) : null;
    setFocusedTeamId(Number.isFinite(tid as number) && (tid as number) > 0 ? tid : null);
  };

  const applyOwnershipPayload = (
    engine: OwnMapEngine,
    res: Awaited<ReturnType<typeof ownershipService.getOwnershipByLeague>>,
    fallbackDate: string,
    seasonKey: number | null | undefined
  ) => {
    // Prefer the scrubber's requested day when the API echoes the same calendar date
    // in another format (ISO timestamp, etc.) — keeps step arrows aligned to `dates`.
    const apiYmd = toYmd(res.date);
    const fallbackYmd = toYmd(fallbackDate);
    const date = fallbackYmd && apiYmd && fallbackYmd === apiYmd
      ? fallbackYmd
      : apiYmd || fallbackYmd || '';
    const own = toOwnMapOwnership(res.ownership, date || null, rosterRef.current);
    engine.setOwnership(own);
    setOwnershipState(own);
    const idx = engine.searchIndex();
    searchAllRef.current = idx;
    const labels: Record<string, string> = {};
    for (const h of idx) labels[h.fips] = h.label;
    setCountyLabels(labels);
    setCountyCount(Object.keys(own.ownership).length);
    if (date) setSelectedDate(date);
    loadedKeyRef.current = `${seasonKey ?? 'current'}:${date || 'latest'}`;
    setPick(null);
    setLoading(false);
    onMapReady?.();
    // setOwnership clears engine focus — restore shareable / sticky team highlight.
    const tid = focusedTeamIdRef.current;
    if (tid != null) {
      const zoomIn = deepLinkFocusZoomRef.current;
      deepLinkFocusZoomRef.current = false;
      requestAnimationFrame(() => {
        if (zoomIn) engine.focusTeam(tid);
        else engine.highlightTeam(tid);
      });
    }
  };

  // Mount: geo + teams + latest ownership in parallel. Seasons/dates never gate first paint.
  useEffect(() => {
    let cancelled = false;
    let engine: OwnMapEngine | null = null;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const stage = stageRef.current;
        const canvas = canvasRef.current;
        const svg = svgRef.current;
        const seamsEl = seamsRef.current;
        const selEl = selRef.current;
        const labelsEl = labelsRef.current;
        const logosEl = logosRef.current;
        if (!stage || !canvas || !svg || !seamsEl || !selEl || !labelsEl || !logosEl) {
          throw new Error('Map stage failed to mount');
        }

        const bootParams = new URLSearchParams(window.location.search);
        const urlSeasonParam = bootParams.get('season');
        const urlSeason =
          urlSeasonParam && /^\d{4}$/.test(urlSeasonParam) ? Number(urlSeasonParam) : undefined;
        const urlDate = toYmd(bootParams.get('date'));
        if (urlSeason != null) {
          setSelectedSeason(urlSeason);
          bootstrappedSeasonRef.current = urlSeason;
        }

        // Critical path — do not await seasons, dates, counties, or ticker.
        const geoP = loadBaseGeo();
        const teamsP = teamService.getTeamsByLeague(LEAGUE_ID, true).catch(() => ({
          success: false as const,
          teams: [] as Team[],
        }));
        const ownershipP = ownershipService.getOwnershipByLeague(
          LEAGUE_ID,
          urlDate || undefined,
          urlSeason
        );
        const seasonsP = leagueService.getSeasons(LEAGUE_ID).catch(() => [] as LeagueSeason[]);
        const datesP = ownershipService
          .getAvailableDates(LEAGUE_ID, urlSeason)
          .catch(() => [] as AvailableDate[]);

        // Drawer population only — defer off the paint path.
        countyService
          .listCounties()
          .then((counties) => {
            if (cancelled) return;
            const pop: Record<string, number | null> = {};
            for (const c of counties) {
              pop[String(c.county_fips).padStart(5, '0')] = c.population;
            }
            setPopByFips(pop);
          })
          .catch(() => undefined);

        const [base, teamsRes] = await Promise.all([geoP, teamsP]);
        if (cancelled) return;

        const rosterById = new Map((teamsRes.teams || []).map((t) => [Number(t.team_id), t]));
        rosterRef.current = rosterById;
        setAreaSqMiByFips(countyAreaSqMi(base.us));

        engine = createOwnMapEngine(
          {
            stage,
            canvas,
            svg,
            seamsEl,
            selEl,
            labelsEl,
            logosEl,
            leagueId: LEAGUE_ID,
            onPick: handlePick,
          },
          base
        );
        engineRef.current = engine;
        if (hideTeamLogos) engine.setShowTeamLogos(false);
        // Partner faces must project as soon as the stage exists. Real colors
        // replace this once ownership arrives; a down API no longer hides pins.
        if (variant === 'partner') {
          engine.setOwnership(neutralOwnership(base.us));
          setLoading(false);
          setEngineReady(true);
          onEngine?.(engine);
          onMapReady?.();
        } else {
          setEngineReady(true);
          onEngine?.(engine);
        }

        seasonsP.then((seasonList) => {
          if (cancelled) return;
          setSeasons(seasonList);
          const picked = pickDefaultSeason(seasonList, urlSeasonParam);
          setSelectedSeason((prev) => {
            const next = prev != null ? prev : picked;
            bootstrappedSeasonRef.current = next;
            // Retarget bootstrap cache key once the concrete season is known.
            if (loadedKeyRef.current && /^(null|current):/.test(loadedKeyRef.current) && next != null) {
              const suffix = loadedKeyRef.current.split(':').slice(1).join(':');
              loadedKeyRef.current = `${next}:${suffix}`;
            }
            return next;
          });
        });

        datesP.then((dateList) => {
          if (cancelled) return;
          const normalized = normalizeDateList(dateList);
          setDates(normalized);
          setSelectedDate((prev) => {
            const prevYmd = toYmd(prev);
            if (prevYmd && normalized.length) {
              const idx = resolveDateIndex(prevYmd, normalized);
              if (idx >= 0) return toYmd(normalized[idx].snapshot_date);
            }
            if (prevYmd) return prevYmd;
            // Default to today's date in America/New_York timezone instead of latest snapshot.
            // The ownership API will carry forward the most recent snapshot's territory.
            const todayET = getTodayEastern();
            if (normalized.length) {
              // Clamp to the latest available snapshot if today is earlier (shouldn't happen)
              const latest = normalized[normalized.length - 1].snapshot_date;
              return todayET < latest ? latest : todayET;
            }
            return todayET;
          });
        });

        try {
          const res = await ownershipP;
          if (cancelled || !engine) return;
          const seasonKey =
            urlSeason ??
            (bootstrappedSeasonRef.current !== undefined
              ? bootstrappedSeasonRef.current
              : null);
          applyOwnershipPayload(engine, res, '', seasonKey);
          if (bootstrappedSeasonRef.current === undefined) {
            bootstrappedSeasonRef.current = seasonKey;
          }
        } catch (ownErr) {
          if (!cancelled) {
            if (variant !== 'partner') {
              setError(
                ownErr instanceof Error ? ownErr.message : 'Failed to load territory ownership'
              );
            }
            setLoading(false);
            onMapReady?.();
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (variant !== 'partner') {
            setError(err instanceof Error ? err.message : 'Failed to load own-map lab');
          }
          setLoading(false);
          onMapReady?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      engine?.destroy();
      engineRef.current = null;
      setEngineReady(false);
      onEngine?.(null);
    };
  }, []);

  useEffect(() => {
    engineRef.current?.setShowTeamLogos(!hideTeamLogos);
  }, [hideTeamLogos]);

  // Keep ?season=&date=&focusTeam= shareable on home.
  useEffect(() => {
    if (!isHome) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (selectedSeason != null) {
      if (next.get('season') !== String(selectedSeason)) {
        next.set('season', String(selectedSeason));
        changed = true;
      }
    }
    const ymd = toYmd(selectedDate);
    if (ymd) {
      if (next.get('date') !== ymd) {
        next.set('date', ymd);
        changed = true;
      }
    } else if (next.has('date')) {
      next.delete('date');
      changed = true;
    }
    if (focusedTeamId != null) {
      if (next.get('focusTeam') !== String(focusedTeamId)) {
        next.set('focusTeam', String(focusedTeamId));
        changed = true;
      }
    } else if (next.has('focusTeam')) {
      next.delete('focusTeam');
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
  }, [isHome, selectedSeason, selectedDate, focusedTeamId, searchParams, setSearchParams]);

  // User (or late default) season change — reload dates + ownership. Skip bootstrap season.
  useEffect(() => {
    if (!engineReady || selectedSeason == null) return;
    if (bootstrappedSeasonRef.current === selectedSeason) return;
    bootstrappedSeasonRef.current = selectedSeason;
    let cancelled = false;
    (async () => {
      try {
        if (variant !== 'partner') setLoading(true);
        setError(null);
        loadedKeyRef.current = null;
        setSelectedDate('');
        setDates([]);
        setOwnershipState(null);
        setPick(null);
        const dateList = normalizeDateList(
          await ownershipService.getAvailableDates(LEAGUE_ID, selectedSeason)
        );
        if (cancelled) return;
        setDates(dateList);
        // Default to today's date in America/New_York timezone instead of latest snapshot.
        const todayET = getTodayEastern();
        const latest = dateList.length ? dateList[dateList.length - 1].snapshot_date : '';
        const defaultDate = latest && todayET < latest ? latest : (todayET || latest);
        setSelectedDate(defaultDate);
        if (!defaultDate) {
          setOwnershipState(null);
          setCountyCount(0);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          if (variant !== 'partner') {
            setError(err instanceof Error ? err.message : 'Failed to load season dates');
          }
          setLoading(false);
          onMapReady?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engineReady, selectedSeason]);

  // Load ownership when the scrubber date changes (or after a season switch sets a date).
  useEffect(() => {
    const engine = engineRef.current;
    if (!engineReady || !engine || !selectedDate) return;
    const ymd = toYmd(selectedDate);
    if (!ymd) return;
    const key = `${selectedSeason ?? 'current'}:${ymd}`;
    if (key === loadedKeyRef.current) return;
    // Bootstrap painted "latest" — adopt the concrete day without a second fetch.
    const seasonPrefix = `${selectedSeason ?? 'current'}:`;
    if (loadedKeyRef.current === `${seasonPrefix}latest`) {
      loadedKeyRef.current = key;
      return;
    }
    const shown = toYmd(ownershipStateRef.current?.snapshotDate);
    if (shown && shown === ymd && loadedKeyRef.current) {
      loadedKeyRef.current = key;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await ownershipService.getOwnershipByLeague(
          LEAGUE_ID,
          ymd,
          selectedSeason ?? undefined
        );
        if (cancelled) return;
        applyOwnershipPayload(engine, res, ymd, selectedSeason);
      } catch (err) {
        if (!cancelled) {
          if (variant !== 'partner') {
            setError(err instanceof Error ? err.message : 'Failed to load ownership date');
          }
          setLoading(false);
          onMapReady?.();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [engineReady, selectedDate, selectedSeason]);

  useEffect(() => {
    if (!isHome) return;
    let cancelled = false;
    recentConquestsService
      .list(24, LEAGUE_ID)
      .then((rows) => {
        if (!cancelled) setConquests(rows);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [isHome]);

  useEffect(() => {
    if (!isHome || !pick || pick.mode !== 'county') {
      setCountySoldiers([]);
      return;
    }
    let cancelled = false;
    enlistService
      .getCounty(pick.fips)
      .then((r) => {
        if (!cancelled) {
          setCountySoldiers(
            (r.soldiers || []).map((s) => ({
              handle: s.handle,
              tier: s.tier,
              team_name: (s as { team_name?: string }).team_name,
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCountySoldiers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isHome, pick]);

  const handleView = (mode: OwnMapViewMode) => {
    setView(mode);
    engineRef.current?.setView(mode);
  };

  const onSearchInput = (value: string) => {
    setQuery(value);
    const v = value
      .toLowerCase()
      .replace(/,/g, ' ')
      .replace(/\s+(county|parish|borough)\b/g, '')
      .trim();
    setActiveHit(0);
    if (v.length < 2) {
      setHits([]);
      return;
    }
    setHits(
      searchAllRef.current
        .filter(
          (h) =>
            h.search.startsWith(v) ||
            h.search.includes(` ${v}`) ||
            h.search.replace(/\s+/g, ' ').startsWith(v.replace(/\s+/g, ' '))
        )
        .slice(0, 8)
    );
  };

  const pickHit = (hit: { fips: string; label: string }) => {
    setQuery(hit.label);
    setHits([]);
    engineRef.current?.focusCounty(hit.fips);
  };

  const { standings, conferenceTotal } = useMemo(() => {
    const ranked = rankEmpiresByCounties(ownershipState, popByFips, areaSqMiByFips);
    const showLand = activeConference !== 'all';
    const filtered = ranked.filter((r) => {
      const roster = rosterRef.current.get(r.teamId);
      const conf = roster?.conference || r.team.conference;
      return conferenceMatches(activeConference, conf);
    });
    const confCounties = filtered.reduce((sum, r) => sum + r.counties, 0);
    const rows = filtered.slice(0, 12).map((r) => {
      const roster = rosterRef.current.get(r.teamId);
      const color = pickMapFillColor({
        mapColor: roster?.map_color ?? r.team.color,
        primary: roster?.primary_color,
        secondary: roster?.secondary_color,
      });
      const pct =
        activeConference === 'all'
          ? r.share * 100
          : confCounties > 0
            ? (r.counties / confCounties) * 100
            : 0;
      const fullName = (roster?.team_name || r.team.name || r.team.short || `Team ${r.teamId}`).trim();
      return {
        teamId: r.teamId,
        team: fullName.toUpperCase(),
        pct,
        color,
        trim: trimColor(color),
      };
    });
    const landBits: string[] = [];
    if (showLand && filtered.length > 0 && Object.keys(popByFips).length > 0) {
      const population = filtered.reduce((sum, r) => sum + r.population, 0);
      landBits.push(`${formatCompactPop(population)} pop`);
    }
    if (showLand && filtered.length > 0 && Object.keys(areaSqMiByFips).length > 0) {
      const areaSqMi = filtered.reduce((sum, r) => sum + r.areaSqMi, 0);
      landBits.push(formatSqMi(areaSqMi));
    }
    return {
      standings: rows,
      conferenceTotal: landBits.length ? landBits.join(' · ') : null,
    };
  }, [ownershipState, activeConference, popByFips, areaSqMiByFips]);

  // Conference filter → map only paints / logos for matching empires.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engineReady || !engine) return;
    if (activeConference === 'all') {
      engine.setVisibleTeamIds(null);
      return;
    }
    const ids: number[] = [];
    rosterRef.current.forEach((team, id) => {
      if (conferenceMatches(activeConference, team.conference)) ids.push(Number(id));
    });
    // Ownership teams may carry conference even if roster missed a row.
    if (ownershipState) {
      for (const t of Object.values(ownershipState.teams)) {
        if (conferenceMatches(activeConference, t.conference) && !ids.includes(t.id)) {
          ids.push(t.id);
        }
      }
    }
    engine.setVisibleTeamIds(ids);
  }, [activeConference, engineReady, ownershipState]);

  const confMeta = BROADCAST_CONFERENCES.find((c) => c.id === activeConference);
  const standingsTitle =
    activeConference === 'all' ? 'D1 STANDINGS' : `${(confMeta?.label || '').toUpperCase()} STANDINGS`;

  const activeSeasonMeta = seasons.find((s) => s.season_year === selectedSeason) || null;
  const { start: windowStart, end: windowEnd } = seasonWindow(dates, activeSeasonMeta);
  const seasonTicks = ticksForWindow(windowStart, windowEnd);
  const seasonLabel = activeSeasonMeta
    ? formatSeasonLabel(activeSeasonMeta)
    : selectedSeason
      ? `${selectedSeason} MBB`
      : 'MBB';
  const seasonOptions = seasons.map((s) => ({
    year: s.season_year,
    label: formatSeasonLabel(s),
  }));

  const timelinePosition = selectedDate
    ? dateToPosition(selectedDate, windowStart, windowEnd)
    : 0;
  const knobDate = selectedDate ? parseYmd(selectedDate) : new Date();
  const knobDay = knobDate.getDate();
  const knobLabel = knobDate
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();

  const handleSeasonChange = (year: number) => {
    if (year === selectedSeason) return;
    setSelectedSeason(year);
  };

  const dateStepIndex = useMemo(
    () => resolveDateIndex(selectedDate, dates),
    [dates, selectedDate]
  );

  const stepDate = (delta: -1 | 1) => {
    if (!dates.length) return;
    const idx = dateStepIndex >= 0 ? dateStepIndex : resolveDateIndex(selectedDate, dates);
    if (idx < 0) return;
    const next = dates[idx + delta];
    if (!next) return;
    const nextYmd = toYmd(next.snapshot_date);
    if (!nextYmd || nextYmd === toYmd(selectedDate)) return;
    setSelectedDate(nextYmd);
  };

  const latest = conquests[0];
  const latestLabel = 'LATEST TAKEOVER';
  const latestText = latest
    ? `${(latest.winner.short_name || latest.winner.team_name || 'TEAM').toUpperCase()} SEIZES ${
        latest.county_count
      } COUNTIES${
        latest.loser?.short_name || latest.loser?.team_name
          ? ` FROM ${(latest.loser.short_name || latest.loser.team_name || '').toUpperCase()}`
          : ''
      }`
    : selectedDate
      ? `${selectedDate} · ${countyCount.toLocaleString()} COUNTIES HELD`
      : 'LOADING THE MAP…';

  const tickerItems = conquests.slice(0, 10).map((c) => {
    const w = (c.winner.short_name || c.winner.team_name || 'TEAM').toUpperCase();
    const l = c.loser?.short_name || c.loser?.team_name;
    return {
      team: w,
      text: l
        ? `TAKE ${c.county_count} FROM ${String(l).toUpperCase()}`
        : `EXPAND → +${c.county_count} COUNTIES`,
    };
  });

  const mapStage = (
    <section
      className="own-map-lab__stage"
      ref={(el) => {
        stageRef.current = el;
      }}
    >
      <canvas ref={canvasRef} id="own-map-cv" />
      <svg ref={svgRef} id="own-map-ov" aria-hidden>
        <defs>
          <filter id="om-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2.2" result="b" />
            <feFlood floodColor="#ff2a2a" floodOpacity=".55" />
            <feComposite in2="b" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="om-silver" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" />
            <stop offset=".45" stopColor="#b9c0c8" />
            <stop offset=".6" stopColor="#eef1f4" />
            <stop offset="1" stopColor="#8c939b" />
          </linearGradient>
        </defs>
        <g id="zg">
          <path id="seams" ref={seamsRef} />
          <path id="sel" ref={selRef} />
        </g>
        <g id="labels" ref={labelsRef} />
      </svg>
      <div className="own-map-lab__logos" ref={logosRef} aria-hidden />
      {isPartner ? mapOverlay : null}

      {loading && (
        <div className="own-map-lab__status">
          <div>{isHome ? 'Loading the map…' : 'Loading NCAA Basketball own-map lab…'}</div>
        </div>
      )}
      {error && (
        <div className="own-map-lab__status error">
          <div>{error}</div>
        </div>
      )}
    </section>
  );

  const drawer =
    pick && isHome && !isPartner ? (
      <TerritoryDrawer
        leagueId={LEAGUE_ID}
        pick={pick}
        ownership={ownershipState}
        popByFips={popByFips}
        countyLabels={countyLabels}
        selectedDate={selectedDate}
        selectedSeason={selectedSeason}
        soldiers={countySoldiers}
        onClose={() => {
          engineRef.current?.clearSelection();
          setPick(null);
        }}
      />
    ) : null;

  if (isHome) {
    return (
      <BroadcastShell
        drawer={chromeHidden ? null : drawer}
        activeConference={activeConference}
        onConference={setActiveConference}
        standings={standings}
        standingsTitle={standingsTitle}
        standingsTotal={conferenceTotal}
        query={query}
        onQueryChange={onSearchInput}
        hits={hits}
        activeHit={activeHit}
        onActiveHit={setActiveHit}
        onPickHit={pickHit}
        seasonLabel={seasonLabel}
        seasons={seasonOptions}
        selectedSeason={selectedSeason}
        onSeasonChange={handleSeasonChange}
        seasonTicks={seasonTicks}
        timelinePosition={timelinePosition}
        knobDay={knobDay}
        knobLabel={knobLabel}
        onTimelinePosition={(p) => {
          const next = positionToNearestDate(p, dates, windowStart, windowEnd);
          const ymd = toYmd(next);
          if (ymd) setSelectedDate(ymd);
        }}
        onStepDate={stepDate}
        canStepPrev={dates.length > 1 && dateStepIndex > 0}
        canStepNext={dates.length > 1 && dateStepIndex >= 0 && dateStepIndex < dates.length - 1}
        latestLabel={latestLabel}
        latestText={latestText}
        tickerItems={tickerItems}
        view={view}
        onView={handleView}
        onSelectTeam={(teamId) => {
          engineRef.current?.focusTeam(teamId);
        }}
        chromeHidden={chromeHidden}
        onShowChrome={() => setChromeHidden(false)}
        mapReady={engineReady && !!ownershipState}
        partner={isPartner ? partner : null}
        onPartnerCta={isPartner ? onPartnerCta : undefined}
        shareSlot={
          isPartner ? null : (
            <OwnMapShareControls
              leagueId={LEAGUE_ID}
              engine={engineReady ? engineRef.current : null}
              ownership={ownershipState}
              pick={pick}
              selectedDate={selectedDate}
              selectedSeason={selectedSeason}
              dates={dates.map((d) => d.snapshot_date)}
              popByFips={popByFips}
              countyLabels={countyLabels}
              chromeHidden={chromeHidden}
              onChromeHidden={setChromeHidden}
              toOwnership={toOwnMapOwnership}
              rosterById={rosterRef.current}
              onFrameOwnership={(own) => {
                engineRef.current?.setOwnership(own);
              }}
              onRestoreOwnership={() => {
                const own = ownershipStateRef.current;
                if (own) engineRef.current?.setOwnership(own);
              }}
            />
          )
        }
      >
        {mapStage}
      </BroadcastShell>
    );
  }

  return (
    <div className="own-map-lab">
      <header className="own-map-lab__bar">
        <div className="own-map-lab__brand">
          <span className="own-map-lab__brand-e">EMPIRE</span>
          <span className="own-map-lab__brand-m">MAPS</span>
        </div>
        <div className="own-map-lab__meta">
          NCAA Basketball · <strong>Own-map lab</strong>
        </div>
        <div className="own-map-lab__views" role="tablist">
          <button type="button" className={view === 'flat' ? 'on' : ''} onClick={() => handleView('flat')}>
            Flat
          </button>
          <button type="button" className={view === 'globe' ? 'on' : ''} onClick={() => handleView('globe')}>
            Globe
          </button>
        </div>
        <div className="own-map-lab__dates">
          <label htmlFor="own-map-season">Season</label>
          <select
            id="own-map-season"
            value={selectedSeason ?? ''}
            onChange={(e) => handleSeasonChange(Number(e.target.value))}
            disabled={!seasons.length}
          >
            {seasons.map((s) => (
              <option key={s.season_year} value={s.season_year}>
                {formatSeasonLabel(s)}
              </option>
            ))}
          </select>
          <label htmlFor="own-map-date">Date</label>
          <select
            id="own-map-date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            disabled={!dates.length}
          >
            {dates.map((d) => (
              <option key={d.snapshot_date} value={d.snapshot_date}>
                {d.snapshot_date}
                {d.is_preseason ? ' (preseason)' : ''}
              </option>
            ))}
          </select>
        </div>
        <p className="own-map-lab__note">
          Debug lab.{' '}
          <Link to={`/league/${LEAGUE_ID}`}>Mapbox map →</Link>
          {' · '}
          <Link to="/">Product home →</Link>
        </p>
      </header>

      <section
        className="own-map-lab__stage"
        ref={(el) => {
          stageRef.current = el;
        }}
      >
        <canvas ref={canvasRef} id="own-map-cv" />
        <svg ref={svgRef} id="own-map-ov" aria-hidden>
          <defs>
            <filter id="om-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feFlood floodColor="#ff2a2a" floodOpacity=".55" />
              <feComposite in2="b" operator="in" />
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="om-silver" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#ffffff" />
              <stop offset=".45" stopColor="#b9c0c8" />
              <stop offset=".6" stopColor="#eef1f4" />
              <stop offset="1" stopColor="#8c939b" />
            </linearGradient>
          </defs>
          <g id="zg">
            <path id="seams" ref={seamsRef} />
            <path id="sel" ref={selRef} />
          </g>
          <g id="labels" ref={labelsRef} />
        </svg>
        <div className="own-map-lab__logos" ref={logosRef} aria-hidden />

        <div className="own-map-lab__search">
          <input
            value={query}
            onChange={(e) => onSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveHit((i) => Math.min(hits.length - 1, i + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveHit((i) => Math.max(0, i - 1));
              } else if (e.key === 'Enter' && hits[activeHit]) {
                pickHit(hits[activeHit]);
              }
            }}
            placeholder="Who owns my county? e.g. Tippecanoe, IN"
            autoComplete="off"
          />
          {hits.length > 0 && (
            <ul>
              {hits.map((h, i) => (
                <li
                  key={h.fips}
                  className={i === activeHit ? 'act' : ''}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickHit(h);
                  }}
                >
                  {h.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        {pick && (
          <TerritoryDrawer
            leagueId={LEAGUE_ID}
            pick={pick}
            ownership={ownershipState}
            popByFips={popByFips}
            countyLabels={countyLabels}
            selectedDate={selectedDate}
            selectedSeason={selectedSeason}
            soldiers={[]}
            onClose={() => {
              engineRef.current?.clearSelection();
              setPick(null);
            }}
          />
        )}

        <div className="own-map-lab__foot">
          {selectedDate
            ? `Ownership snapshot ${selectedDate} · ${countyCount.toLocaleString()} counties · self-hosted`
            : 'Loading…'}
        </div>

        {loading && (
          <div className="own-map-lab__status">
            <div>Loading NCAA Basketball own-map lab…</div>
          </div>
        )}
        {error && (
          <div className="own-map-lab__status error">
            <div>{error}</div>
          </div>
        )}
      </section>
    </div>
  );
};

export default OwnMapLab;
